import { Injectable } from "@angular/core";
import * as signalR from "@microsoft/signalr";
import { IHttpConnectionOptions, LogLevel } from "@microsoft/signalr";
import { environment } from "environments/environment";
import { BehaviorSubject, EMPTY, Observable, Subject, throwError } from "rxjs";
import { catchError, take } from "rxjs/operators";

export enum SignalrStates {
    Connecting = 0,
    Connected = 1,
    Reconnecting = 2,
    Disconnected = 4
}

@Injectable({
    providedIn: 'root',
})
export class SignalrCoreServiceBase implements signalR.ILogger {
    protected jwtToken: string | null;

    /**
     * Returns the token handed to SignalR's accessTokenFactory. SignalR invokes the factory
     * on every negotiate/reconnect, so overrides MUST return the LIVE token (not a cached copy).
     * The default implementation returns the legacy cached jwtToken for backwards compatibility;
     * it is null before a session exists, so buildConnection() normalizes it to '' for the factory.
     */
    protected getAccessToken(): string | null {
        return this.jwtToken;
    }

    /**
     * Refreshes the access token after a start() failure caused by an expired token. The default
     * implementation fails fast; subclasses that own an AuthenticationService override this hook.
     * Keeping it concrete (instead of abstract) preserves the injectable base contract and avoids
     * forcing every subclass to implement it.
     */
    protected refreshToken(): Observable<string> {
        return throwError(new Error('refreshToken() is not implemented by this SignalR service'));
    }

    private statoConnessione = new BehaviorSubject<SignalrStates>(SignalrStates.Disconnected);
    public get statoConnessione$() {
        return this.statoConnessione.asObservable();
    }

    protected hubConnection: signalR.HubConnection;

    protected buildConnection() {
        const options: IHttpConnectionOptions = {
            // accessTokenFactory must yield a string: normalize the nullable token to '' when
            // no session is loaded so SignalR never sends a literal "Bearer null" header.
            accessTokenFactory: () => {
                return this.getAccessToken() ?? '';
            }
        };

        this.hubConnection = new signalR.HubConnectionBuilder()
            .withAutomaticReconnect()
            .configureLogging(this)
            .withUrl(`${environment.coreApiUrl}/hubs/push-data`, options)
            .build();

        this.hubConnection.onclose((error: Error) => {
            console.log(`%cSIGNALR onclose - error: ${error.message}`, 'background: red; color: white');
            this.statoConnessione.next(SignalrStates.Disconnected);
        });

        this.hubConnection.onreconnecting((error?: Error) => {
            console.log(`%cSIGNALR onreconnecting - error: ${error.message}`, 'background: yellow; color: black');
            this.statoConnessione.next(SignalrStates.Reconnecting);
        });

        this.hubConnection.onreconnected((connectionId?: string) => {
            console.log(`%cSIGNALR onreconnected - connectionId: ${connectionId}`, 'background: green; color: white');
            this.statoConnessione.next(SignalrStates.Connected);
        });

        return this;
    }

    protected startConnection() {
        this.startConnectionWithRetry(0);
    }

    /**
     * Starts the hub connection. When the initial attempt fails with HTTP 401 the access token is
     * refreshed once and start() is retried exactly once, so an expired token cannot keep the hub
     * permanently offline. Any other failure, a failed refresh, or a failing retry is logged and
     * abandoned (never loops).
     */
    private startConnectionWithRetry(attempt: number): void {
        this.hubConnection
            .start()
            .then(() => {
                console.log('Connection started');
                this.statoConnessione.next(SignalrStates.Connected);
            })
            .catch((error: Error) => this.handleStartError(error, attempt));
    }

    private handleStartError(error: Error, attempt: number): void {
        if (attempt === 0 && this.isUnauthorizedError(error)) {
            console.warn('SignalR start failed with 401 — refreshing token and retrying once');

            this.refreshToken()
                .pipe(
                    take(1),
                    catchError((refreshError: Error) => {
                        console.error(`SignalR token refresh failed, aborting retry: ${refreshError.message}`);
                        return EMPTY;
                    })
                )
                .subscribe(() => {
                    // The refreshed auth state may already have restarted the hub through its
                    // isAuthenticated listener; retry only if the connection is still down.
                    if (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
                        return;
                    }
                    this.startConnectionWithRetry(1);
                });
            return;
        }

        console.error(`Error while starting connection (attempt ${attempt}): ${error.message}`);
    }

    /**
     * Robust 401 detection: SignalR wraps HTTP failures in HttpError (statusCode), while some
     * transports surface a plain Error whose message embeds the status code.
     */
    private isUnauthorizedError(error: Error): boolean {
        if (error instanceof signalR.HttpError) {
            return error.statusCode === 401;
        }
        return error instanceof Error && error.message.includes('401');
    }

    public log(logLevel: LogLevel, message: string) {
        // console.log(`%cSIGNALR[${logLevel}]: ${message}`, 'background: green; color: white');
        if (logLevel === LogLevel.Trace) return;
        console.log(`SIGNALR LOG-LEVEL[${logLevel}]: ${message}`);
    }

    protected subscribeMessage<T>(methodName: string) {
        const sub = new Subject<T>();
        this.hubConnection.on(methodName, (data) => {
            console.log(methodName, data);
            sub.next(data);
        });

        return sub;
    }
}